import React, {
  forwardRef,
  ForwardedRef,
  useImperativeHandle,
  useState,
} from 'react';
import { useTestRuleMutation, RuleMatchResult } from 'src/store/matchRuleApi';
import { useFetchManyUsersQuery } from 'src/store/userApi';
import { Panel } from 'src/ui/Panel';
import { Field } from 'src/ui/Field';
import { Select } from 'src/ui/Select';
import { EmptyState } from 'src/ui/EmptyState';
import { Spinner } from 'src/ui/Spinner';
import { Badge } from 'src/ui/Badge';
import { useToast } from 'src/ui/Toast';

export type TestRuleRef = {
  onTestRule: (id: number) => void;
};

const TestRule = forwardRef((props, ref: ForwardedRef<TestRuleRef>) => {
  const toast = useToast();
  const [userAId, setUserAId] = useState<string>('');
  const [userBId, setUserBId] = useState<string>('');
  const { data: users } = useFetchManyUsersQuery({ limit: 100 });
  const [testMatch, testMatchStatus] = useTestRuleMutation();

  useImperativeHandle(
    ref,
    () => ({
      onTestRule: async (id: number) => {
        if (!userAId || !userBId) {
          toast({
            type: 'error',
            title: 'Select both users before running the test',
          });
          return;
        }
        testMatch({ id, userAId, userBId });
      },
    }),
    [testMatch, toast, userAId, userBId]
  );

  const result = testMatchStatus.data as RuleMatchResult | undefined;

  const scoreColor = (s: number) =>
    s >= 70 ? 'green' : s >= 40 ? 'amber' : 'red';

  const headerRight = result ? (
    <span
      className="mono"
      style={{ fontWeight: 700, color: 'var(--accent-text)' }}
    >
      A:{result.scoreA}% / B:{result.scoreB}%
    </span>
  ) : undefined;

  return (
    <Panel
      className="grow"
      title="Test Rule"
      icon="target"
      scroll
      headerRight={headerRight}
    >
      <div className="col gap-3">
        <div className="row gap-2">
          <div className="grow">
            <Field label="User A">
              <Select
                size="sm"
                value={userAId}
                onChange={(e) => setUserAId(e.target.value)}
              >
                <option value="">Select User A</option>
                {users?.data?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.email}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="grow">
            <Field label="User B">
              <Select
                size="sm"
                value={userBId}
                onChange={(e) => setUserBId(e.target.value)}
              >
                <option value="">Select User B</option>
                {users?.data?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.email}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>

        <div
          style={{
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-muted)',
            marginTop: -6,
          }}
        >
          Select a rule in the editor then click "Test" to run it against these
          two users.
        </div>

        {testMatchStatus.isLoading && (
          <div
            className="row"
            style={{ justifyContent: 'center', padding: '32px 0' }}
          >
            <Spinner />
          </div>
        )}

        {!testMatchStatus.isLoading && !result && (
          <EmptyState
            icon="target"
            title="No result yet"
            desc="Pick two users, select a rule in the editor, and click Test."
          />
        )}

        {result && (
          <div className="col gap-3">
            {/* Scores */}
            <div className="row gap-2">
              <div
                style={{
                  flex: 1,
                  background: 'var(--bg-muted)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '8px 10px',
                }}
              >
                <div
                  className="text-muted"
                  style={{ fontSize: 'var(--fs-2xs)' }}
                >
                  User A
                </div>
                <div className="row gap-2" style={{ marginTop: 4 }}>
                  <span
                    className="mono"
                    style={{ fontWeight: 700, fontSize: 'var(--fs-lg)' }}
                  >
                    {result.scoreA}%
                  </span>
                  <Badge color={scoreColor(result.scoreA)} size="sm">
                    {result.gradeA}
                  </Badge>
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  background: 'var(--bg-muted)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '8px 10px',
                }}
              >
                <div
                  className="text-muted"
                  style={{ fontSize: 'var(--fs-2xs)' }}
                >
                  User B
                </div>
                <div className="row gap-2" style={{ marginTop: 4 }}>
                  <span
                    className="mono"
                    style={{ fontWeight: 700, fontSize: 'var(--fs-lg)' }}
                  >
                    {result.scoreB}%
                  </span>
                  <Badge color={scoreColor(result.scoreB)} size="sm">
                    {result.gradeB}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Deal breaker */}
            {result.isDealBreaker && (
              <Badge color="red" solid>
                Deal Breaker
              </Badge>
            )}

            {/* Prompt */}
            {result.prompt && (
              <div
                style={{
                  borderTop: '1px solid var(--border)',
                  paddingTop: 10,
                }}
              >
                <div className="eyebrow" style={{ marginBottom: 6 }}>
                  Prompt used
                </div>
                <div
                  className="mono"
                  style={{
                    fontSize: 'var(--fs-2xs)',
                    color: 'var(--text-secondary)',
                    background: 'var(--bg-muted)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '8px 10px',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {result.prompt}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
});

export default TestRule;
